import i18n from '../i18n'
import { imeiAPI } from '../api/client'
import type { ImeiHubServiceId } from '../components/ImeiHubSignalIcon'
import { formatApiError } from './apiError'
import { storePendingImeiCheckPayment } from './pendingImeiCheckPayment'

export type HubCatalogState = {
  paidPriceCents: Partial<Record<ImeiHubServiceId, number>>
  stripeCheckoutEnabled: boolean
}

export function authTokenForImeiHub(token?: string | null): string | null {
  if (token) return token
  try {
    return localStorage.getItem('auth_token')
  } catch {
    return null
  }
}

export async function loadHubCatalogState(): Promise<HubCatalogState> {
  const empty: HubCatalogState = { paidPriceCents: {}, stripeCheckoutEnabled: false }
  try {
    const res = await imeiAPI.getCatalog()
    const data = res.data as {
      stripe_enabled?: boolean
      services?: { id: string; paid?: boolean; price_eur_cents?: number | null }[]
    }
    const paidPriceCents: Partial<Record<ImeiHubServiceId, number>> = {}
    for (const row of data.services ?? []) {
      if (row.paid && typeof row.price_eur_cents === 'number') {
        paidPriceCents[row.id as ImeiHubServiceId] = row.price_eur_cents
      }
    }
    return {
      paidPriceCents,
      stripeCheckoutEnabled: Boolean(data.stripe_enabled),
    }
  } catch {
    return empty
  }
}

export function hubServicePaidInCatalog(
  catalog: HubCatalogState,
  service: ImeiHubServiceId,
): boolean {
  return catalog.paidPriceCents[service] != null
}

/** Open the branded checkout page (Stripe Embedded Checkout on the right). */
export async function redirectToImeiHubCheckout(
  imeiDigits: string,
  service: ImeiHubServiceId,
  token?: string | null,
): Promise<void> {
  storePendingImeiCheckPayment({
    imei: imeiDigits,
    service,
    startedAt: new Date().toISOString(),
  })
  const locale = (i18n.language || 'lt').slice(0, 5)
  const res = await imeiAPI.createCheckCheckout(imeiDigits, service, token, 'web', locale)
  const url = (res.data as { checkout_url?: string }).checkout_url
  if (!url) throw new Error('missing checkout_url')
  window.location.href = url
}

export function formatImeiHubCheckError(
  err: unknown,
  t: (key: string) => string,
  fallbackKey = 'vc.imeiHub.result.error',
): string {
  const status = (err as { response?: { status?: number } }).response?.status
  const detail = (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
  if (status === 401 || detail === 'login_required') return t('vc.imeiHub.result.authRequired')
  if (status === 402 || detail === 'payment_required') return t('vc.imeiHub.result.paymentRequired')
  if (status === 503 && detail === 'stripe_not_configured') return t('vc.imeiHub.result.stripeOff')
  if (status === 503 && detail === 'stripe_publishable_key_missing') {
    return t('vc.imeiHub.result.stripeOff')
  }
  if (status === 503 && detail === 'hub_checkout_db_unavailable') {
    return t('vc.imeiHub.result.hubCheckoutDbUnavailable')
  }
  if (status === 502 && detail === 'stripe_checkout_failed') {
    return t('vc.imeiHub.result.stripeCheckoutFailed')
  }
  if (status === 403 && detail === 'site_gate_required') return t('vc.imeiHub.result.siteGateRequired')
  if (status === 429) return t('vc.imeiHub.result.rateLimit')
  const raw = formatApiError(err, t(fallbackKey))
  if (raw === 'payment_required') return t('vc.imeiHub.result.paymentRequired')
  if (raw === 'Not authenticated') return t('vc.imeiHub.result.authRequired')
  return raw
}
